import React, { Children } from 'react';
import { withTranslation } from 'react-i18next';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import { Button, ButtonGroup, ButtonToolbar } from 'reactstrap';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faCalendarDay, faCalendarWeek } from '@fortawesome/free-solid-svg-icons';
import { Link } from '@s20/components/common/Link';
import i18n from '@s20/i18n';
import { appendDispatchTimeZone as appendDispatchTimeZoneUtil } from '@s20/common/util/date';
import { httpClient } from '@s20/services/httpClient';
import { FlexCenterRow, FlexStartRow } from '../common/forms/FormElements';
import authService from '../api-authorization/AuthorizeService';
import { BaseFormViewModel } from '../common/ViewModel';
import CommonContext, { ApiRoutes, AppNavPaths } from '../Common';
import './AssignmentCalendar.scss';

const localizer = momentLocalizer(moment);

const navigate = {
    PREVIOUS: 'PREV',
    NEXT: 'NEXT',
    TODAY: 'TODAY',
    DATE: 'DATE',
};

const views = [
    'month',
    'week',
    'day',
];

const api_date_format = 'YYYYMMDD';

const getIcon = (view) => {
    if (view === 'month') {
        return faCalendarAlt;
    }

    if (view === 'day') {
        return faCalendarDay;
    }

    return faCalendarWeek;
};

class AssignmentToolbar extends React.Component {
    static contextType = CommonContext;

    navigate = (action) => {
        this.props.onNavigate(action);
    };

    render() {
        const { label } = this.props;
        if (!(this.context ?? {}).user) return null;
        return (
            <ButtonToolbar className="mb-2 d-flex flex-row">
                <ButtonGroup size="sm">

                    <Button
                        color="outline-primary p-1"
                        onClick={this.navigate.bind(null, navigate.PREVIOUS)}
                    >
                        <i className="fa fa-step-backward" />
                    </Button>
                    <Button
                        color="outline-primary p-1"
                        onClick={this.navigate.bind(null, navigate.TODAY)}
                    >
                        <small>{i18n.t('calendar:today').toUpperCase()}</small>
                    </Button>
                    <Button
                        color="outline-primary p-1"
                        onClick={this.navigate.bind(null, navigate.NEXT)}
                    >
                        <i className="fa fa-step-forward" />
                    </Button>
                </ButtonGroup>
                <div className="rbc-toolbar-label d-flex flex-column flex-fill align-items-center justify-content-center">
                    <strong>{label}</strong>
                    <small>{this.context.user.fullName}</small>
                </div>

                <ButtonGroup size="sm">
                    {views.map((view) => (
                        <Button
                            color="outline-primary text-capitalize"
                            key={view}
                            onClick={() => this.props.onView(view)}
                        >
                            <FontAwesomeIcon
                                className="text-muted"
                                icon={getIcon(view)}
                                size="lg"
                            />
                        </Button>
                    ))}
                </ButtonGroup>
            </ButtonToolbar>
        );
    }
}

const AssignmentEvent = ({ event }) => {
    const jobTitle = `${event.jobTitle}${(event.subcontractorName ? ` - ${event.subcontractorName}` : '')}`;

    let url = '';

    if (event.timesheetId > 0) {
        url = `${AppNavPaths.Timesheet}/${event.timesheetId}/${event.assignmentId}`;
    } else {
        // confirm
        if (event.isCancellation) {
            url = `${AppNavPaths.CancelConfirmation}/${event.assignmentCancellationId}`;
        } else {
            url = `${AppNavPaths.AssignmentConfirmation}/${event.assignmentId}`;
        }
    }
    return (
        <FlexStartRow
            component={Link}
            to={url}
        >
            <span>{event.title}</span>
            <span className="ml-1 event-job-title">{i18n.t('calendar:eventTitleWithJob', { title: jobTitle })}</span>
        </FlexStartRow>
    );
};

class CalendarErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { error: null };
    }

    static getDerivedStateFromError(error) {
        return { error };
    }

    componentDidCatch(error, info) {
        console.error('AssignmentCalendar render error:', error, info);
    }

    render() {
        if (this.state.error) {
            return (
                <div className="alert alert-warning m-3">
                    <strong>{i18n.t('calendar:errorBoundary')}</strong>
                    <pre
                        className="mt-2 mb-0"
                        style={{ whiteSpace: 'pre-wrap' }}
                    >
                        {String(this.state.error?.message ?? this.state.error)}
                    </pre>
                </div>
            );
        }
        return this.props.children;
    }
}

const CURRENT_DATE = moment();

// Date cell wrapper for conditional background coloring
const ColoredDateCellWrapper = ({ children, value }) => {
    let backgroundColor = 'white';

    if (CURRENT_DATE.isAfter(moment(value), 'day')) {
        backgroundColor = '#DDD';
    } else if (CURRENT_DATE.isSame(moment(value), 'day')) {
        backgroundColor = '#c0e1fa';
    }

    return React.cloneElement(Children.only(children), {
        style: {
            ...children.style,
            backgroundColor,
        },
    });
};

class AssignmentCalendar extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.formRef = React.createRef();

        const stateBase = {
            events: [], // list of calendar events
            user: null,
            calendarAssignments: [],
            view: 'month',
            defaultView: 'month',
            startDate: moment().startOf('month').format(api_date_format),
            endDate: moment().endOf('month').format(api_date_format),
            ...new BaseFormViewModel(),
        };

        this.state = stateBase;
    }

    componentDidMount() {
        this._subscription = authService.subscribe(() => this.populateState());
        this.populateState();
    }

    onClearErrors = () => this.setState({ errors: {} });

    onNavigate = (date) => {
        this.setState({ date },
            () => this.getAssignments());
    };

    getAssignments = async () => {
        this.setState({ loading: true });
        const { date, view } = { ...this.state };

        // Start and end dates to include extra days visible in the current view
        const start = moment(date).startOf(view).startOf('week').format(api_date_format);
        const end = moment(date).endOf(view).endOf('week').format(api_date_format);

        let calendarAssignments;
        try {
            [calendarAssignments] = await Promise.all([
                httpClient.js(ApiRoutes.calendar.get(start, end)),
            ]);
        } catch (err) {
            console.error('[calendar] fetch failed', err);
            this.setState({ events: [], loading: false, calendarAssignments: [] });
            return;
        }

        const events = [];
        for (const x of calendarAssignments ?? []) {
            try {
                const m = moment(x.day);
                if (!m.isValid()) {
                    console.warn('[calendar] skip invalid date', { day: x.day, assignmentId: x.assignmentId });
                    continue;
                }
                // Format in 'en' so the title is stable across locales (downstream
                // slice/parse logic depends on the English am/pm marker).
                let title = m.clone().locale('en').format('h:mma');
                // let title = time.slice(0, -1);
                try {
                    title = appendDispatchTimeZoneUtil(title, x.dayOffset, x.timeZone, x.dispatchTimeZone, 'h:mma');
                } catch (innerErr) {
                    console.warn('[calendar] appendTimeZone failed', {
                        day: x.day,
                        dayOffset: x.dayOffset,
                        timeZone: x.timeZone,
                        innerErr,
                    });
                }
                const eventStart = m.toDate();
                events.push({
                    ...x,
                    title,
                    start: eventStart,
                    end: eventStart,
                    allDay: false,
                    resource: null,
                });
            } catch (err) {
                console.error('[calendar] event transform threw', { x, err });
            }
        }

        this.setState({
            events,
            loading: false,
            calendarAssignments,
        });
    };

    setSaving = (b) => this.setState({ saving: b });

    changeView = (view) => {
        this.setState({ view });
    };

    customEventPropGetter = (event) => {
        let statusClass = '';

        if (event.rejected) statusClass = 'rejected';
        else statusClass = event.confirmed ? 'confirmed' : 'unconfirmed';

        return { className: `${this.state.view} ${statusClass}` };
    };

    async populateState() {
        const isAuthenticated = await authService.isAuthenticated();
        if (isAuthenticated) {
            this.getAssignments();
        }
    }

    render() {
        const {
            events,
        } = this.state;
        const { t } = this.props;

        if (!(this.context ?? {}).user) return null;

        const calendarMessages = {
            allDay: t('calendar:allDay'),
            previous: t('calendar:previous'),
            next: t('calendar:next'),
            today: t('calendar:today'),
            month: t('calendar:month'),
            week: t('calendar:week'),
            day: t('calendar:day'),
            agenda: t('calendar:agenda'),
            date: t('calendar:date'),
            time: t('calendar:time'),
            event: t('calendar:event'),
            noEventsInRange: t('calendar:noEventsInRange'),
            showMore: (count) => t('calendar:showMore', { count }),
        };

        return (
            <CalendarErrorBoundary>
                <div className="assignmentCalendarPage calendar-content p-1">
                    <Calendar
                        selectable
                        toolbar
                        views={views}
                        defaultView="month"
                        localizer={localizer}
                        startAccessor="start"
                        endAccessor="end"
                        events={events}
                        messages={calendarMessages}
                        longPressThreshold={75}
                        eventPropGetter={this.customEventPropGetter.bind(this)}
                        components={{ toolbar: AssignmentToolbar, event: AssignmentEvent, dateCellWrapper: ColoredDateCellWrapper }}
                        onView={this.changeView.bind(this)}
                        onNavigate={this.onNavigate}
                    />
                    <FlexCenterRow className="confirm-legend">
                        <small className="unconfirmed">{t('calendar:legendUnconfirmed')}</small>
                        <small className="confirmed">{t('calendar:legendConfirmed')}</small>
                        <small className="rejected ml-2">{t('calendar:legendTimeRejected')}</small>
                    </FlexCenterRow>
                </div>
            </CalendarErrorBoundary>
        );
    }
}

export default withTranslation()(AssignmentCalendar);
